// src/components/Home.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Layout,
  Typography,
  Card,
  Button,
  Space,
  Collapse,
  Form,
  Input,
  Alert,
} from "antd";
import "antd/dist/reset.css";
import "./Home.css";
import sliceVideo from '../images/brandsenpai-slice.mp4';

import emailjs from "emailjs-com";
import { message } from "antd";

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

export default function Home() {
  const year = useMemo(() => new Date().getFullYear(), []);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    document.title = "BrandSenpai";
  }, []);

  // Scroll-reveal animations (kept from your version)
  useEffect(() => {
    const selectors = [
      ".bs-section h2",
      ".bs-hero-card",
      ".bs-kpi .item",
      ".bs-cards .ant-card",
      ".bs-check",
      ".bs-price",
      "#faq .ant-collapse-item",
      "#contact .ant-card",
    ];
    const nodes = Array.from(document.querySelectorAll(selectors.join(",")));
    nodes.forEach((el, i) => {
      el.classList.add("reveal", "fade-up");
      el.style.setProperty("--delay", `${Math.min(i * 60, 600)}ms`);
    });
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );
    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Basic sanitizer: trim, collapse spaces, strip angle brackets
  const sanitize = (v) =>
    (v ?? "")
      .toString()
      .trim()
      .replace(/\s+/g, " ")
      .replace(/[<>]/g, "");

  const onSend = async (values) => {
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.error("Missing EmailJS env vars", {
        SERVICE_ID,
        TEMPLATE_ID,
        PUBLIC_KEY,
      });
      message.error("Email service not configured. Check .env and restart.");
      return;
    }

    // Sanitize and map to EmailJS template variables
    const templateParams = {
      from_name: sanitize(values.name),
      from_email: sanitize(values.email),
      message: sanitize(values.msg),
    };

    setSending(true);
    try {
      const res = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );
      console.log("EmailJS response", res);
      message.success("Sent! We’ll reply to your email shortly.");
      setSent(true);
      form.resetFields();
      // Auto-hide the banner
      setTimeout(() => setSent(false), 8000);
    } catch (err) {
      console.error("EmailJS error", err?.text || err);
      message.error("Sorry, something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "transparent" }}>
      <Content className="bs-root">
        {/* HERO */}
       <section
  id="home"
  className="bs-container bs-section"
  style={{ paddingTop: 24 }}
>
  {/* <div style={{ marginBottom: 24 }}>
    <video
  autoPlay
  loop
  muted
  playsInline
  className="hero-slice-video"
>
  <source src={sliceVideo} type="video/mp4" />
  Your browser does not support the video tag.
</video>
  </div> */}
          <div className="bs-hero-grid">
            <div>
              <span className="bs-badge">
                Mentor-led Marketing • Full-stack Creative • Measurable Growth
              </span>
              <Title
                level={1}
                style={{
                  margin: "12px 0 10px",
                  fontSize: "clamp(36px,5vw,58px)",
                  lineHeight: 1.05,
                }}
              >
                Your Digital Growth Guide
              </Title>
              <Paragraph className="bs-sub">
                BrandSenpai blends <b>strategy</b>, <b>creativity</b>, and{" "}
                <b>technology</b> to amplify your brand's online presence. From
                positioning and identity to websites, content, and performance
                marketing—we help you <b>scale, stand out, and succeed</b>.
              </Paragraph>
              <Space wrap className="cta-row" style={{ marginTop: 22 }}>
                <Button href="#contact" className="bs-ghost" type="primary" size="large">
                  Book a Free Consult
                </Button>
                <Button href="#services" className="bs-ghost" type="primary"  size="large">
                  See How We Help
                </Button>
              </Space>

              <div className="bs-kpi">
                <div className="item">
                  <div className="num">+312%</div>
                  <div>avg. traffic lift</div>
                </div>
                <div className="item">
                  <div className="num">8.4x</div>
                  <div>ROAS best case</div>
                </div>
                <div className="item">
                  <div className="num">
                    <span aria-hidden>★</span> 4.9
                  </div>
                  <div>client rating</div>
                </div>
              </div>
            </div>

            <div className="bs-hero-card">
              <Title level={3} style={{ margin: 0 }}>
                What we do
              </Title>
              <div style={{ marginTop: 12 }}>
                {[
                  "Brand Strategy",
                  "Visual Identity",
                  "Web Design & Dev",
                  "Cinematics",
                  "Marketing",
                  "Social & Ads",
                  "Analytics",
                ].map((t) => (
                  <span className="bs-pill" key={t}>
                    {t}
                  </span>
                ))}
              </div>
              <Paragraph style={{ margin: "18px 0 0", color: "#c7cbe0" }}>
                One partner. End-to-end execution. Mentor support tailored for{" "}
                <b>startups, SMEs, and personal brands</b>.
              </Paragraph>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="bs-section">
          <div className="bs-container">
            <Title level={2}>Services that compound your growth</Title>
            <div className="bs-cards">
              {[
                {
                  icon: "📐",
                  h: "Brand Strategy",
                  p: "Positioning, messaging, and a clear story that makes your audience choose you.",
                },
                {
                  icon: "🎬",
                  h: "Cinematics",
                  p: "Professional videography and photography services to enhance your marketing and brand presence.",
                },
                {
                  icon: "💻",
                  h: "Websites that Convert",
                  p: "Beautiful and captivating UI with modern UX, designed to capture your audience’s attention, built for SEO and measurable outcomes",
                },
                {
                  icon: "🎨",
                  h: "Identity & Design",
                  p: "Logos, style systems, and on-brand assets designed for clarity and recall.",
                },
                
                {
                  icon: "✍️",
                  h: "Content & SEO",
                  p: "Authority-building content and technical SEO to grow traffic sustainably.",
                },
                {
                  icon: "📣",
                  h: "Social & Performance Ads",
                  p: "Creative campaigns across Meta, Instagram and TikTok that drive qualified demand.",
                },
                
              ].map((c) => (
                <Card key={c.h} className="bs-card" bordered={false} bodyStyle={{ padding: 22 }}>
                  <div className="bs-icon" aria-hidden>
                    {c.icon}
                  </div>
                  <Title level={3} style={{ margin: "6px 0 8px" }}>
                    {c.h}
                  </Title>
                  <Paragraph style={{ color: "#c7cbe0", marginBottom: 0 }}>
                    {c.p}
                  </Paragraph>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section id="process" className="bs-section">
          <div className="bs-container bs-grid2">
            <div>
              <Title level={2}>Mentor-led process, built for momentum</Title>
              {[
                {
                  b: "Discover",
                  t: "Workshops to align goals, audience, and differentiation.",
                },
                {
                  b: "Design",
                  t: "Identity, copy, and web UX that communicate value clearly.",
                },
                {
                  b: "Deploy",
                  t: "Launch with SEO, analytics, and ad foundations in place.",
                },
                {
                  b: "Mentor",
                  t: "Ongoing coaching, experiments, and content cadence to scale.",
                },
              ].map((it) => (
                <div className="bs-check" key={it.b}>
                  <span>✅</span>
                  <div>
                    <b>{it.b}</b>
                    <br />
                    {it.t}
                  </div>
                </div>
              ))}
            </div>

            
          </div>
        </section>

        {/* WORK */}
        <section id="work" className="bs-section">
          <div className="bs-container">
            <Title level={2}>Selected work</Title>
            <div className="bs-cards">
              {[
                {
                  h: "Websites",
                  p: "From simple personal pages to complex web systems. Fast, secure, and fully managed on the cloud.",
                },
                {
                  h: "Cinematics",
                  p: "From single videos to bundled packages tailored to your goals. Scripted, shot, and edited — professionally from start to finish.",
                },
                {
                  h: "Marketing",
                  p: "From social media campaigns to video ads and landing pages. Designed to attract, engage, and drive real results.",
                },
              ].map((w) => (
                <Card key={w.h} className="bs-card" bordered={false} bodyStyle={{ padding: 22 }}>
                  <Title level={3} style={{ margin: 0 }}>
                    {w.h}
                  </Title>
                  <Paragraph style={{ color: "#c7cbe0", marginTop: 8 }}>
                    {w.p}
                  </Paragraph>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="bs-section">
          <div className="bs-container">
            <Title level={2}>Website 💻</Title>
            <div className="bs-pricing">
              {/* Starter */}
              <Card className="bs-price" bordered={false} bodyStyle={{ padding: 26 }}>
                <Paragraph className="card-badge-bounce1">Basic</Paragraph>
                <div className="big">$800</div>
               
                <ul>
                  <li>1-page website / landing page</li>
                  <li>Mobile responsive</li>
                  <li>Basic SEO + analytics setup</li>
                  <li>Standard hosting setup</li>
                </ul>
                <div style={{ marginTop: 12 }}>
                  <Button href="#contact" className="btn-gradient" type="primary">
                    Choose Basic
                  </Button>
                </div>
              </Card>

              {/* Growth */}
              <Card
                className="bs-price"
                bordered
                bodyStyle={{ padding: 26 }}
                style={{ borderColor: "rgba(124,58,237,.5)" }}
              >
                <Paragraph className="card-badge-bounce1">Standard</Paragraph>
                <div className="big">$2,800</div>
               
                <ul>
                  <li>Up to 4-page website</li>
                  <li>Mobile responsive</li>
                  <li>Basic SEO + analytics setup</li>
                  <li>Standard hosting setup</li>
                </ul>
                <div style={{ marginTop: 12 }}>
                  <Button href="#contact" className="btn-gradient" type="primary">
                    Choose Standard
                  </Button>
                </div>
              </Card>

              {/* Scale */}
            

              <Card className="bs-price" bordered={false} bodyStyle={{ padding: 26 }}>
                <Paragraph className="card-badge-bounce1">Custom</Paragraph>
                <div className="big1">Custom Quote</div>
                


                <ul>
                  <li>E-commerce (shop, payment gateway, inventory)</li>
                  <li>Custom Backend (Database)</li>
                  <li>Advanced integrations (CRM, APIs)</li>
                  <li>Ongoing support & scaling</li>
                </ul>
                <div style={{ marginTop: 12 }}>
                  <Button href="#contact" className="btn-gradient" type="primary">
                    Request Proposal
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </section>

              <section id="pricing" className="bs-section">
          <div className="bs-container">
            <Title level={2}>Cinematics 🎬</Title>
            <div className="bs-pricing">
              {/* Starter */}
              <Card className="bs-price" bordered={false} bodyStyle={{ padding: 26 }}>
                <Paragraph className="card-badge-bounce"
>
  Alacarte
</Paragraph>

                <div className="big">$550</div>
               
                <ul>
          
                  <li>Pre-Production (conceptualizing, scripting, revisions)</li>
                  <li>Production (direction, filming)</li>
                  <li>Post-Production (editing, motion, graphics,revisions)</li>
                  
                </ul>
                <div style={{ marginTop: 12 }}>
                  <Button href="#contact" className="btn-gradient1" type="primary">
                    Choose Alacarte
                  </Button>
                </div>
              </Card>

              {/* Growth */}
              <Card
                className="bs-price"
                bordered
                bodyStyle={{ padding: 26 }}
                style={{ borderColor: "rgba(124,58,237,.5)" }}
              >
                <Paragraph className="card-badge-bounce"
>
  Package 6
</Paragraph>
                <div className="big">$2,500</div>
                <div></div>
                <card><ul>
                  <li>Package of 6 Videos at discounted rate</li>
                  <li>Includes all features in services in Single Video</li>
                 <li>Save up to $80</li>
                
                </ul></card>
                
                <div style={{ marginTop: 12 }}>
                  <Button href="#contact" className="btn-gradient1" type="primary">
                    Choose Package
                  </Button>
                </div>
              </Card>

              {/* Scale */}
              <Card
  className="bs-price custom-highlight"
  bordered={false}
  bodyStyle={{ padding: 26 }}
>
  <Paragraph className="card-badge-bounce">Custom</Paragraph>
  
  <div className="big">Custom Quote</div>
  <ul>
    
    <li>Flexible number of videos based on your needs</li>
    <li>Package pricing based on total quantity & complexity</li>
    
  </ul>
  <div style={{ marginTop: 12 }}>
    <Button href="#contact" className="btn-gradient1" type="primary">
      Request Proposal
    </Button>
  </div>
</Card>

            </div>
          </div>
        </section>


        {/* FAQ */}
        <section id="faq" className="bs-section">
          <div className="bs-container">
            <Title level={2}>Questions we get a lot</Title>
            <Collapse ghost style={{ background: "transparent" }}>
              <Panel header={<b>What makes BrandSenpai different?</b>} key="1">
                <Paragraph>
                  We pair execution with mentorship. You don’t just get
                  assets—you gain the skills and systems to keep growing.
                </Paragraph>
              </Panel>
              <Panel header={<b>Do you work with solo professionals?</b>} key="2">
                <Paragraph>
                  Absolutely. Our target audience extends from solo professionals to businesses.
                </Paragraph>
              </Panel>
              <Panel header={<b>How fast can we launch?</b>} key="3">
                <Paragraph>
                  Starter sites ship in ~2–3 weeks depending on content
                  readiness. Growth & Scale timelines are scoped during
                  discovery.
                </Paragraph>
              </Panel>
            </Collapse>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="bs-section">
          <div className="bs-container bs-grid2">
            <div>
              <Title level={2}>Let’s build your personal brand or digital system</Title>
              <Paragraph className="bs-sub">
                Tell us about your goals and we’ll send a tailored plan within
                48 hours.
              </Paragraph>
              <ul style={{ margin: "14px 0 0 18px", color: "#c7cbe0" }}>
                <li>Website</li>
                <li>Cinematics</li>
                <li>IT solutions</li>
                <li>No hard sell—just clarity and next steps</li>
              </ul>
            </div>

            <Card className="bs-card" bordered={false} bodyStyle={{ padding: 22 }}>
              {sent && (
                <Alert
                  type="success"
                  showIcon
                  message="Message sent!"
                  description="Thanks for reaching out — we’ll email you back shortly."
                  className="bs-success-black"
                  style={{ marginBottom: 12}}
                />
              )}
              <Form form={form} layout="vertical" requiredMark={false} onFinish={onSend}>
                <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                  <Input
                    placeholder="Jane Tan"
                    size="large"
                    className="input-dark"
                  />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ required: true, type: "email" }]}
                >
                  <Input
                    placeholder="jane@company.com"
                    size="large"
                    className="input-dark"
                  />
                </Form.Item>

                <Form.Item
                  label="What do you need?"
                  name="msg"
                  rules={[{ required: true }]}
                >
                  <TextArea
                    rows={5}
                    placeholder="Website redesign, SEO, and ongoing content. Launch date: November."
                    className="input-dark"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    htmlType="submit"
                    className="bs-ghost" type="primary" 
                    size="large"
                    loading={sending}
                    disabled={sending}
                  >
                    {sending ? "Sending..." : "Send Request"}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </section>
      </Content>
    </Layout>
  );
}
